<template>
  <div class="pt-3">
    <div class="title mb-3">{{ $t('sendDeal.title') }}</div>

    <!-- Modalità arbitro non giocatore: tutti i board della sessione corrente senza carte -->
    <template v-if="isTdNotPlaying">
      <div v-if="tdBoardsWithoutDeal.length > 0" class="mt-3">
        <div class="subtitle mb-3">{{ $t('sendDeal.selectBoard') }}</div>
        <select-with-buttons :options="tdBoardOptions" @click="goToDealEntry"></select-with-buttons>
      </div>
      <div v-else class="info-message">
        {{ $t('sendDeal.noBoards') }}
      </div>
    </template>

    <!-- Modalità giocatore normale: solo board del round attivo -->
    <template v-else>
      <div v-if="activeRoundEntries.length === 0" class="info-message">
        {{ $t('sendDeal.noRound') }}
      </div>
      <div v-else-if="boardsWithoutDeal.length > 0">
        <div class="subtitle mb-3">{{ $t('sendDeal.selectBoard') }}</div>
        <select-with-buttons :options="boardOptions" @click="goToDealEntry"></select-with-buttons>
      </div>
      <div v-else class="info-message">
        {{ $t('sendDeal.noBoards') }}
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SelectWithButtons from '@/components/lib/SelectWithButtons';

export default {
  name: 'SendDeal',
  components: { SelectWithButtons },
  data() {
    return {
      selectedPhase: 1,
      selectedSession: 1,
    };
  },
  computed: {
    ...mapGetters({
      playerCurrentSessionMap: 'players/playerCurrentSessionMap',
      globalDeals: 'deals/played',
      isTd: 'webApp/isTd',
      isPlaying: 'webApp/isPlaying',
    }),

    isTdNotPlaying() {
      return this.isTd && !this.isPlaying;
    },

    // ── Modalità arbitro: fase e sessione corrente ─────────────────────────

    /** Tutti i board della fase/sessione corrente senza carte caricate (deduplicati) */
    tdBoardsWithoutDeal() {
      const ranksets = this.globalDeals
        ?.Phases?.[this.selectedPhase]
        ?.Sessions?.[this.selectedSession]
        ?.Ranksets ?? {};

      const seen = new Set();
      const result = [];

      for (const rkset of Object.values(ranksets)) {
        const boards = rkset.Boards ?? {};
        const deals  = rkset.Deals  ?? {};

        for (const boardNumber of Object.keys(boards)) {
          if (seen.has(boardNumber)) continue;
          seen.add(boardNumber);

          const deal = deals[boardNumber];
          if (!deal || !deal.Hand || !deal.Hand.N) {
            result.push(Number(boardNumber));
          }
        }
      }

      return result.sort((a, b) => a - b);
    },

    tdBoardOptions() {
      return this.tdBoardsWithoutDeal.map(board => ({
        value: board,
        text: board,
        class: ['scoreButton']
      }));
    },

    // ── Logica round attivo per giocatore normale ───────────────────────────

    /**
     * Round "attuale": il round con il numero più alto in cui il giocatore
     * ha caricato almeno un risultato (PlayedBoards.length > 0).
     * Finché non viene inviato almeno un risultato del round successivo,
     * rimane il round corrente.
     */
    activeRound() {
      const roundsWithBoards = this.playerCurrentSessionMap
        .filter(el => el.PlayedBoards && el.PlayedBoards.length > 0)
        .map(el => el.round);
      if (roundsWithBoards.length === 0) return null;
      return Math.max(...roundsWithBoards);
    },

    activeRoundEntries() {
      if (this.activeRound === null) return [];
      return this.playerCurrentSessionMap.filter(el => el.round === this.activeRound);
    },

    playedBoardsInActiveRound() {
      const seen = new Set();
      return this.activeRoundEntries
        .flatMap(el => el.PlayedBoards || [])
        .filter(b => {
          if (seen.has(b)) return false;
          seen.add(b);
          return true;
        });
    },

    boardsWithoutDeal() {
      return this.playedBoardsInActiveRound.filter(boardNumber => {
        const entry = this.activeRoundEntries.find(
          el => (el.PlayedBoards || []).includes(boardNumber)
        );
        if (!entry) return false;
        const deal = this.globalDeals
          ?.Phases?.[entry.phase]
          ?.Sessions?.[entry.session]
          ?.Ranksets?.[entry.rankset]
          ?.Deals?.[boardNumber];
        return !deal || !deal.Hand || !deal.Hand.N;
      });
    },

    boardOptions() {
      return this.boardsWithoutDeal
        .slice()
        .sort((a, b) => a - b)
        .map(board => ({
          value: board,
          text: board,
          class: ['scoreButton']
        }));
    }
  },
  methods: {
    goToDealEntry(boardNumber) {
      const board = Number(boardNumber);
      if (!Number.isInteger(board) || board < 1) {
        return;
      }
      this.$router.push({ name: 'dealEntry', params: { boardNumber: board } });
    }
  },
  mounted() {
    if (this.isTdNotPlaying) {
      // Posiziona automaticamente su fase e sessione corrente (max disponibile)
      this.$nextTick(() => {
        const phases = Object.keys(this.globalDeals.Phases).map(Number);
        if (phases.length === 0) return;
        this.selectedPhase = Math.max(...phases);
        this.$nextTick(() => {
          const sessions = Object.keys(
            this.globalDeals.Phases[this.selectedPhase]?.Sessions ?? {}
          ).map(Number);
          if (sessions.length > 0) {
            this.selectedSession = Math.max(...sessions);
          }
        });
      });
    }
  }
};
</script>

<style scoped>
* { text-align: center; }

.title {
  width: 100%;
  margin: 0 auto;
  text-align: center;
  color: #3396B8;
  font-size: 1.2rem;
}

.subtitle {
  color: #555;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.info-message {
  font-size: 1.1rem;
  color: #888;
  margin-top: 2rem;
}

>>> .scoreButton {
  background: white;
  border: 1px solid #3396B8;
}
</style>
